import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCities,
  fetchStates,
} from "../../../components/entities/address/addressesSlice";
import { setEmployeeData, updateEmployee } from "./employeeSlice";
import { useParams } from "react-router-dom";

function validNumber(x) {
  const out = /^(?!\s*$)[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/.test(x || "");
  return out;
}

const useEditEmployeeAddress = () => {
  const dispatch = useDispatch();
  const [reset, setReset] = useState(false);
  const [geninfo, setGeninfo] = useState({});
  const [stateData, setStateData] = useState([]);
  const [isSaveBtn, setIsSaveBtn] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [isStateLoading, setIsStateLoading] = useState(false);
  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);

  const toastRef = useRef(null);
  const { id } = useParams();

  const {
    address,
    statesarray,
    cityarray,
    isDataAvailable,
    activeTab,
    isEdit,
    employeeDetails,
  } = useSelector((state) => state.employeeregister);

  useEffect(() => {
    dispatch(setEmployeeData({ key: "isEdit", value: false }));
  }, [dispatch]);

  const initializeAddressData = () => ({
    address_line_1: employeeDetails?.address_line1 || "",
    address_line_2: employeeDetails?.address_line2 || "",
    city: employeeDetails?.city || "",
    country: employeeDetails?.country || "",
    state: employeeDetails?.state_province || "",
    postalcode: employeeDetails?.zip_code || "",
  });

  useEffect(() => {
    const initialData = initializeAddressData();
    setGeninfo(initialData);
    dispatch(setEmployeeData({ key: "address", value: initialData }));
  }, [employeeDetails, dispatch]);

  const handleEnableEdit = () => {
    setReset(false);
    setIsSaveBtn(true);

    dispatch(setEmployeeData({ key: "isEdit", value: true }));
  };

  const onChangeAddress = async (name, value) => {
    if (name === "country") {
      dispatch(setEmployeeData({ key: "cityarray", value: [] }));
      dispatch(setEmployeeData({ key: "statesarray", value: [] }));

      const countryCode = typeof value === "object" ? value.code : value;

      setGeninfo((prev) => ({ ...prev, country: value, state: "", city: "" }));

      setIsStateLoading(true);
      const stateRes = await dispatch(fetchStates(countryCode)).unwrap();
      const stateOptions = stateRes.map((state) => ({
        name: state.label,
        code: state.value,
      }));
      dispatch(setEmployeeData({ key: "statesarray", value: stateOptions }));
      setIsStateLoading(false);
      dispatch(setEmployeeData({ key: "cityarray", value: [] }));
      return;
    }

    if (name === "state") {
      dispatch(setEmployeeData({ key: "cityarray", value: [] }));
      const countryCode =
        typeof geninfo.country === "object"
          ? geninfo.country.code
          : geninfo.country;
      const stateCode = typeof value === "object" ? value.code : value;

      setGeninfo((prev) => ({ ...prev, state: value, city: "" }));
      setIsCityLoading(true);
      const cityRes = await dispatch(
        fetchCities([countryCode, stateCode]),
      ).unwrap();
      const cityOptions = cityRes.map((city) => ({
        name: city.label,
        code: city.value,
      }));
      dispatch(setEmployeeData({ key: "cityarray", value: cityOptions }));
      setIsCityLoading(false);
      return;
    }

    setGeninfo((prev) => ({ ...prev, [name]: value }));
  };

  const fetchDropdownData = async (addressInfo) => {
    const countryCode =
      typeof addressInfo?.country === "object"
        ? addressInfo.country.code
        : addressInfo.country;
    const stateCode =
      typeof addressInfo?.state_province === "object"
        ? addressInfo.state_province.code
        : addressInfo.state_province;
    const cityCode =
      typeof addressInfo?.city === "object"
        ? addressInfo.city.code
        : addressInfo.city;

    let stateOptions = addressInfo?.stateOptions;
    let cityOptions = addressInfo?.cityOptions;

    if (!stateOptions && countryCode) {
      setIsStateLoading(true);
      const stateRes = await dispatch(fetchStates(countryCode)).unwrap();
      stateOptions = stateRes.map((state) => ({
        name: state.label,
        code: state.value,
      }));
      dispatch(setEmployeeData({ key: "statesarray", value: stateOptions }));
      setIsStateLoading(false);
    }

    if (!cityOptions && countryCode && stateCode) {
      setIsCityLoading(true);
      const cityRes = await dispatch(
        fetchCities([countryCode, stateCode]),
      ).unwrap();
      cityOptions = cityRes.map((city) => ({
        name: city.label,
        code: city.value,
      }));
      dispatch(setEmployeeData({ key: "cityarray", value: cityOptions }));
      setIsCityLoading(false);
    }

    const selectedState = stateOptions?.find(
      (state) => state.code === stateCode,
    );
    const selectedCity = cityOptions?.find((city) => city.code === cityCode);
    setStateData(stateOptions);
    setGeninfo((prev) => ({
      ...prev,
      state: selectedState,
      city: selectedCity,
    }));
  };

  useEffect(() => {
    if (employeeDetails) {
      fetchDropdownData(employeeDetails);
    }
  }, [employeeDetails]);
  const hydrateAddressData = async (rawAddress) => {
    const countryCode =
      typeof rawAddress.country === "object"
        ? rawAddress.country.code
        : rawAddress.country;

    const stateCode =
      rawAddress.state && rawAddress.state.code
        ? rawAddress.state.code
        : rawAddress.state;

    const cityCode =
      rawAddress.city && rawAddress.city.code
        ? rawAddress.city.code
        : rawAddress.city;

    let stateOptions = rawAddress.stateOptions;
    let cityOptions = rawAddress.cityOptions;

    if (!stateOptions && countryCode) {
      const stateRes = await dispatch(fetchStates(countryCode)).unwrap();
      stateOptions = stateRes.map((state) => ({
        name: state.label,
        code: state.value,
      }));
      dispatch(setEmployeeData({ key: "statesarray", value: stateOptions }));
    }

    if (!cityOptions && countryCode && stateCode) {
      const cityRes = await dispatch(
        fetchCities([countryCode, stateCode]),
      ).unwrap();
      cityOptions = cityRes.map((city) => ({
        name: city.label,
        code: city.value,
      }));
      dispatch(setEmployeeData({ key: "cityarray", value: cityOptions }));
    }

    const selectedState = stateOptions?.find(
      (state) => state.code === stateCode,
    );
    const selectedCity = cityOptions?.find((city) => city.code === cityCode);

    return {
      ...rawAddress,
      state: selectedState || null,
      city: selectedCity || null,
    };
  };

  const handleDiscardEdit = async () => {
    setReset(true);
    setIsSaveBtn(false);
    dispatch(
      setEmployeeData({
        key: "isEdit",
        value: false,
      }),
    );
    const hydrated = await hydrateAddressData(initializeAddressData());
    dispatch(setEmployeeData({ key: "address", value: hydrated }));
    setGeninfo(hydrated);
    dispatch(
      setEmployeeData({
        key: "address",
        value: hydrated,
      }),
    );
  };

  const codeOf = (v) =>
    typeof v === "object" && v ? (v.code ?? v.value ?? "") : (v ?? "");

  const norm = (data = {}) =>
    JSON.stringify({
      address_line_1: data?.address_line_1?.trim() || "",
      address_line_2: data?.address_line_2?.trim() || "",
      country: codeOf(data?.country),
      state: codeOf(data?.state ?? data?.state_province),
      city: codeOf(data?.city),
      postalcode: data?.postalcode?.trim() || "",
    });

  const isValid = (data) =>
    !!codeOf(data?.country) &&
    !!data?.address_line_1?.trim() &&
    !!data?.postalcode?.trim() &&
    validNumber(data?.postalcode);

  useEffect(() => {
    const current = norm(geninfo);
    const baseline = norm({
      address_line_1: address?.address_line_1 ?? employeeDetails?.address_line1,
      address_line_2: address?.address_line_2 ?? employeeDetails?.address_line2,
      country: address?.country ?? employeeDetails?.country,
      state_province:
        address?.state ??
        address?.state_province ??
        employeeDetails?.state_province,
      city: address?.city ?? employeeDetails?.city,
      postalcode: address?.postalcode ?? employeeDetails?.zip_code,
    });

    const isIdentical = current !== baseline;
    const isInfoValid = isValid(geninfo);

    setIsSaveBtn(!(isIdentical && isInfoValid && !isUpdating));
  }, [geninfo, address, employeeDetails, isUpdating]);

  const handleUpdateAddress = async () => {
    try {
      const data = {
        address_line1: geninfo?.address_line_1 || "",
        address_line2: geninfo?.address_line_2 || "",
        city:
          typeof geninfo.city === "object" ? geninfo?.city?.code : geninfo.city,
        country:
          typeof geninfo.country === "object"
            ? geninfo?.country?.code
            : geninfo.country,
        state_province:
          typeof geninfo.state === "object"
            ? geninfo?.state?.code
            : geninfo.state,
        zip_code: geninfo?.postalcode || "",
      };

      setIsUpdating(true);
      dispatch(setEmployeeData({ key: "isUpdating", value: true }));

      await dispatch(
        updateEmployee({ id, data: { ...employeeDetails, ...data } }),
      ).unwrap();

      toastRef.current?.show({
        severity: "success",
        summary: "Location & Address",
        detail: "has been updated!",
        icon: "pi pi-check",
      });

      dispatch(setEmployeeData({ key: "isEdit", value: false }));
      dispatch(
        setEmployeeData({
          key: "employeeDetails",
          value: {
            ...employeeDetails,
            ...data,
            state_province: geninfo?.state,
            city: geninfo?.city,
          },
        }),
      );
    } catch (error) {
      console.error("Error updating address:", error);
    } finally {
      dispatch(setEmployeeData({ key: "isUpdating", value: false }));
      setIsUpdating(false);
    }
  };
  return {
    onChangeAddress,
    address,
    statesarray,
    cityarray,
    handleEnableEdit,
    handleDiscardEdit,
    handleUpdateAddress,
    setAuditSidebarVisible,
    toastRef,
    reset,
    isDataAvailable,
    genstate: {
      isCityLoading,
      isStateLoading,
      stateData,
      isDataAvailable,
      activeTab,
      isEdit,
      geninfo,
      isSaveBtn,
      isUpdating,
      isAuditSidebarVisible,
    },
  };
};

export default useEditEmployeeAddress;
